import StyledBooleanCheckbox from '@/components/styled/StyledBooleanCheckbox';
import StyledButton from '@/components/styled/StyledButton';
import StyledInput from '@/components/styled/StyledInput';
import StyledRadioGroup from '@/components/styled/StyledRadioGroup';
import StyledTooltipContainer from '@/components/styled/StyledTooltipContainer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Control,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import PlusIcon from '@/assets/icons/plus.svg?react';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import { useTranslation } from '@/i18n';
import { SupportedLocale } from '@/types/supported-locale.type';
import StyledTranslatableInput from '@/components/styled/StyledTranslatableInput';
import { CarRentalFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { useMemo } from 'react';
import { useTranslationFieldArray } from '@/hooks/useTranslationFieldArray';

interface CategoryTaxesListProps {
  control: Control<CarRentalFormDataWithTranslations>;
  index: number;
  language: SupportedLocale;
  isViewMode?: boolean;
}

const CategoryTaxesList = ({
  control,
  index,
  language,
  isViewMode = false,
}: CategoryTaxesListProps) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `carCategories.${index}.taxes`,
  });
  const { append: appendTranslation, remove: removeTranslation } =
    useTranslationFieldArray({
      control,
      name: `carCategories.${index}.taxes`,
    });
  return (
    <>
      {fields.map((item, taxIndex) => (
        <CategoryTaxCard
          key={item.id}
          control={control}
          categoryIndex={index}
          taxIndex={taxIndex}
          deleteTaxes={() => {
            remove(taxIndex);
            removeTranslation(taxIndex);
          }}
          language={language}
          isViewMode={isViewMode}
        />
      ))}
      {!isViewMode && (
        <StyledButton
          className="text-brand-600 w-fit"
          title={t('carRental.categoryForm.addTax')}
          onClick={() => {
            append({
              name: undefined,
              amount: undefined,
              billingType: undefined,
              required: false,
            } as CarRentalFormDataWithTranslations['carCategories'][number]['taxes'][number]);
            appendTranslation(() => ({
              name: undefined,
            }));
          }}
          variant="outlined"
          icon={<PlusIcon className="size-5 fill-brand-600" />}
        />
      )}
    </>
  );
};

export default CategoryTaxesList;

interface CategoryTaxesCardProps {
  control: Control<CarRentalFormDataWithTranslations>;
  categoryIndex: number;
  taxIndex: number;
  language: SupportedLocale;
  deleteTaxes: () => void;
  isViewMode?: boolean;
}

const CategoryTaxCard = ({
  control,
  categoryIndex,
  taxIndex,
  deleteTaxes,
  language,
  isViewMode = false,
}: CategoryTaxesCardProps) => {
  const { t } = useTranslation();
  const currentTitle = useWatch({
    control,
    name: `carCategories.${categoryIndex}.taxes.${taxIndex}.name`,
  });
  const translations = useWatch({
    control,
    name: 'translations',
  });
  const { setValue } = useFormContext<CarRentalFormDataWithTranslations>();

  const cardTitle = useMemo(() => {
    const currentTranslation =
      translations?.[language]?.carCategories?.[categoryIndex]?.taxes?.[
        taxIndex
      ];
    const currentTitleTranslated = currentTranslation?.name ?? currentTitle;
    return (
      currentTitleTranslated ||
      currentTitle ||
      t('carRental.categoryForm.tax', { index: taxIndex + 1 })
    );
  }, [currentTitle, categoryIndex, taxIndex, language, t, translations]);

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row justify-between">
        <CardTitle className="text-base leading-6 hover:cursor-pointer">
          {cardTitle}
        </CardTitle>
        <div className="flex gap-10">
          <div className="flex gap-4">
            {taxIndex > 0 && !isViewMode && (
              <StyledTooltipContainer
                tooltipMessage={t('carRental.categoryForm.delete')}
              >
                <TrashIcon
                  className="size-5 stroke-brand-600 hover:cursor-pointer"
                  onClick={deleteTaxes}
                />
              </StyledTooltipContainer>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <StyledTranslatableInput
            control={control}
            originalName={`carCategories.${categoryIndex}.taxes.${taxIndex}.name`}
            title={t('carRental.categoryForm.name')}
            name={`carCategories.${categoryIndex}.taxes.${taxIndex}.name`}
            setValue={setValue}
            language={language}
            placeholder={t('carRental.categoryForm.taxNamePlaceholder')}
            disabled={isViewMode}
          />
          <div className="flex flex-col gap-1">
            <Label className="text-gray-700">
              {t('carRental.categoryForm.amount')}
            </Label>
            <div className="flex flex-col md:flex-row md:justify-between">
              <div className="flex gap-6 w-full items-center">
                <div className="min-w-20 max-w-40">
                  <StyledInput
                    control={control}
                    name={`carCategories.${categoryIndex}.taxes.${taxIndex}.amount`}
                    placeholder={t('carRental.categoryForm.amountPlaceholder')}
                    type="number"
                    min={0}
                    disabled={isViewMode}
                  />
                </div>
                <StyledRadioGroup
                  control={control}
                  name={`carCategories.${categoryIndex}.taxes.${taxIndex}.billingType`}
                  options={[
                    {
                      label: t('carRental.categoryForm.billingTypePerDay'),
                      value: 'perDay',
                    },
                    {
                      label: t('carRental.categoryForm.billingTypePerRental'),
                      value: 'perRental',
                    },
                  ]}
                  className="w-1/2"
                  disabled={isViewMode}
                />
              </div>
              <StyledBooleanCheckbox
                control={control}
                name={`carCategories.${categoryIndex}.taxes.${taxIndex}.required`}
                optionLabel={t('carRental.categoryForm.required')}
                className="flex items-center"
                disabled={isViewMode}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
